import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { SizesType } from 'types/Sizes';
import { cssTheme } from '../../../../themes';
import { Divider } from '../../Divider';
export type TitleBarVariant = 'default' | 'inverse' | 'floating';
type TitleBarSize = Extract<SizesType, 'md' | 'lg'>;
export interface TitleBarProps {
  /** The TitleBar variant, options are default or inverse */
  variant?: TitleBarVariant;
  /** Content placed before the center content. It can be any type of React Element */
  startContent?: ReactNode;
  /** Content placed after the center content. It can be any type of React Element */
  endContent?: ReactNode;
  /** Content of the Title */
  centerContent?: ReactNode;
  /** Extra content at the bottom of the TitleBar */
  bottomContent?: ReactNode;
  /** Whether the bottomContent (subHeader) should have paddings around */
  bottomContentPadding?: boolean;
  /** Determine if a divider is rendered in the bottom of the TitleBar */
  hasDivider?: boolean;
  /** Pass through className to allow styles overrides */
  className?: string;
  /** Adds equal horizontal margins on both sides */
  centerAligned?: boolean;
  /** The startContent is removed and the Title is aligned to the left */
  leftAligned?: boolean;
  /** The size of the PopUp header */
  size?: TitleBarSize;
}
const getBackground = (variant: TitleBarVariant) => {
  if (variant === 'inverse') {
    return cssTheme.colors.background.inverse.c950;
  } else if (variant === 'floating') {
    return cssTheme.colors.transparent;
  }
  return cssTheme.colors.background.neutral.c000;
};
const _exp = /*#__PURE__*/() => ({
  variant
}) => variant === 'floating' ? 'absolute' : 'relative';
const _exp2 = /*#__PURE__*/() => ({
  variant
}) => getBackground(variant ?? 'default');
export const TitleBarWrapper = /*#__PURE__*/styled('div')({
  name: "TitleBarWrapper",
  class: "tzq8uon",
  propsAsIs: false,
  vars: {
    "tzq8uon-0": [_exp()],
    "tzq8uon-1": [_exp2()]
  }
});
export const HeaderWrapper = /*#__PURE__*/styled('div')({
  name: "HeaderWrapper",
  class: "haioixw",
  propsAsIs: false
});
const _exp8 = /*#__PURE__*/() => ({
  size
}) => size === 'lg' ? cssTheme.layout.spacing.s250 : cssTheme.layout.spacing.s200;
export const StartIconWrapper = /*#__PURE__*/styled('div')({
  name: "StartIconWrapper",
  class: "s6lia4p",
  propsAsIs: false,
  vars: {
    "s6lia4p-0": [_exp8()]
  }
});
const _exp9 = /*#__PURE__*/() => ({
  size
}) => size === 'lg' ? cssTheme.layout.spacing.s250 : cssTheme.layout.spacing.s200;
export const EndIconWrapper = /*#__PURE__*/styled('div')({
  name: "EndIconWrapper",
  class: "e3rpejc",
  propsAsIs: false,
  vars: {
    "e3rpejc-0": [_exp9()]
  }
});
const _exp10 = /*#__PURE__*/() => ({
  startWidth,
  endWidth,
  leftAligned
}) => `calc(100% - ${leftAligned ? '0' : startWidth}px - ${endWidth}px)`;
const _exp11 = /*#__PURE__*/() => ({
  startWidth,
  leftAligned
}) => leftAligned ? '0' : startWidth;
const _exp12 = /*#__PURE__*/() => ({
  endWidth
}) => endWidth;
const _exp14 = /*#__PURE__*/() => ({
  leftAligned
}) => leftAligned ? 'flex-start' : 'center';
const _exp16 = /*#__PURE__*/() => ({
  variant
}) => variant === 'inverse' ? cssTheme.colors.content.neutral.c000 : cssTheme.colors.content.neutral.c950;
export const CenterContentWrapper = /*#__PURE__*/styled('div')({
  name: "CenterContentWrapper",
  class: "cvi6wkk",
  propsAsIs: false,
  vars: {
    "cvi6wkk-0": [_exp10()],
    "cvi6wkk-1": [_exp11(), "px"],
    "cvi6wkk-2": [_exp12(), "px"],
    "cvi6wkk-3": [_exp14()],
    "cvi6wkk-4": [_exp16()]
  }
});
const _exp20 = /*#__PURE__*/() => ({
  variant
}) => variant === 'inverse' ? cssTheme.colors.content.neutral.c000 : cssTheme.colors.content.neutral.c950;
export const SubHeader = /*#__PURE__*/styled('div')({
  name: "SubHeader",
  class: "s6fmldd",
  propsAsIs: false,
  vars: {
    "s6fmldd-0": [_exp20()]
  }
});
export const TitleBar = ({
  variant = 'default',
  startContent,
  endContent,
  hasDivider = false,
  centerContent,
  bottomContent,
  bottomContentPadding = true,
  centerAligned = true,
  leftAligned = false,
  size = 'md',
  className
}: TitleBarProps) => {
  const [startContentWidth, setStartContentWidth] = useState(0);
  const [endContentWidth, setEndContentWidth] = useState(0);
  const startContentRef = useRef<null | HTMLDivElement>(null);
  const endContentRef = useRef<null | HTMLDivElement>(null);
  useEffect(() => {
    if (startContentRef.current) {
      setStartContentWidth(startContentRef.current.offsetWidth);
    }
    if (endContentRef.current) {
      setEndContentWidth(endContentRef.current.offsetWidth);
    }
  }, []);
  const sideContentWidth: number = Math.max(startContentWidth, endContentWidth);
  const startWidth = centerAligned ? sideContentWidth : startContentWidth;
  const endWidth = centerAligned ? sideContentWidth : endContentWidth;
  return <>
      <TitleBarWrapper data-id="TitleBar" variant={variant} className={className}>
        <HeaderWrapper className={`--size-${size}`}>
          <CenterContentWrapper className={`--size-${size}`} variant={variant} startWidth={startWidth} endWidth={endWidth} leftAligned={leftAligned} data-id="TitleBarTitle">
            {centerContent}
          </CenterContentWrapper>
          {startContent && !leftAligned && <StartIconWrapper ref={startContentRef} className={className} size={size}>
              {startContent}
            </StartIconWrapper>}
          {endContent && <EndIconWrapper ref={endContentRef} size={size} className={className}>
              {endContent}
            </EndIconWrapper>}
        </HeaderWrapper>
        {Boolean(bottomContent) && <SubHeader variant={variant} className={cx(className, bottomContentPadding && '--bottom-content-padding')} data-id="TitleBarSubtitle">
            {bottomContent}
          </SubHeader>}
      </TitleBarWrapper>
      {hasDivider && <Divider />}
    </>;
};

require("./index.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");